import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CaretLeft,
  CaretRight,
  DotsThree,
  Funnel,
  MapPin,
  Plus,
  Sparkle,
  Tag,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiError } from '@/api/client';
import {
  ACTIVITY_TYPES,
  createActivity,
  deleteActivity,
  listActivitiesInRange,
  rescheduleActivity,
  updateActivity,
  type ActivityInput,
  type ActivityType,
} from '@/api/activities';
import { listTeams } from '@/api/teams';
import {
  hasOpponent,
  persistMatchDetails,
  type MatchDetailsValue,
} from '@/api/fixtures';
import { getAvailability, listLocations, type AvailabilityResponse } from '@/api/locations';
import { useActivityFixtures } from '@/hooks/useActivityFixtures';
import { useOrgCrest } from '@/hooks/useOrgCrest';
import { Input } from '@/components/ui/input';
import { ActivityFormModal } from '@/components/schedule/ActivityFormModal';
import { ActivitySheet } from '@/components/schedule/ActivitySheet';
import {
  AvailabilityBlockNotice,
  useAvailabilityBlockNotice,
} from '@/components/schedule/AvailabilityBlockNotice';
import { CalendarFilterPanel } from '@/components/schedule/CalendarFilterPanel';
import { FilterDrawer } from '@/components/schedule/FilterDrawer';
import { LocationsPanel } from '@/components/schedule/LocationsPanel';
import {
  ScheduleCalendar,
  type ScheduleView,
} from '@/components/schedule/ScheduleCalendar';
import { ResourcesView } from '@/components/schedule/ResourcesView';
import { ApplyTemplateModal, TemplateEditorView } from '@/components/schedule/TemplateEditorView';
import { TemplateGenerateDialog } from '@/components/schedule/TemplateGenerateDialog';
import { TemplatesView } from '@/components/schedule/TemplatesView';
import { createTemplate, listTemplates } from '@/api/templates';

// Two-axis navigation:
//   mode   = 'calendar' | 'template'  — primary toggle (top-right pill)
//   view   = 'week' | 'list' | 'month' | 'resources'  — calendar sub-view tab
//           (only meaningful when mode === 'calendar'; ignored otherwise)
// Defaults (mode=calendar, view=week) are omitted from the URL so the
// canonical landing URL stays /orgs/<slug>/schedule.
type Mode = 'calendar' | 'template';
const MODES: Mode[] = ['calendar', 'template'];
type CalendarView = ScheduleView | 'resources';
const CALENDAR_VIEWS: CalendarView[] = ['week', 'list', 'month', 'resources'];

// Schedule is the single workspace for activities, templates, and locations.
// In calendar mode the tab strip switches between week/list/month/resources;
// in template mode the canvas swaps for the template list/editor. Locations
// open as a right-side sheet (transient overlay, not a destination).
export function Schedule() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Legacy deep-links (e.g. notification bell URLs predating the
  // mode/view split) used ?view=templates. Translate them transparently
  // so an old bookmarked or emailed URL keeps working.
  const rawView = searchParams.get('view');
  const legacyTemplateView = rawView === 'templates';

  const mode: Mode = (() => {
    if (legacyTemplateView) return 'template';
    const m = searchParams.get('mode');
    return (MODES as string[]).includes(m ?? '') ? (m as Mode) : 'calendar';
  })();
  const view: CalendarView = (() => {
    if (legacyTemplateView) return 'week';
    return (CALENDAR_VIEWS as string[]).includes(rawView ?? '')
      ? (rawView as CalendarView)
      : 'week';
  })();
  const calendarView: ScheduleView =
    view === 'resources' ? 'week' : (view as ScheduleView);
  const selectedTemplateId = searchParams.get('template');

  const tabListRef = useRef<HTMLDivElement>(null);
  const tabGliderRef = useRef<HTMLSpanElement>(null);
  const modeTabListRef = useRef<HTMLDivElement>(null);
  const modeTabGliderRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const list = tabListRef.current;
    const glider = tabGliderRef.current;
    if (!list || !glider) return;
    const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;
    glider.style.width = `${active.offsetWidth}px`;
    glider.style.transform = `translateX(${active.offsetLeft}px)`;
  }, [view, mode]);
  useEffect(() => {
    const list = modeTabListRef.current;
    const glider = modeTabGliderRef.current;
    if (!list || !glider) return;
    const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;
    glider.style.width = `${active.offsetWidth}px`;
    glider.style.transform = `translateX(${active.offsetLeft}px)`;
  }, [mode]);

  // Locations panel is a transient overlay rather than a destination, so
  // we keep it in local state. The redirect from /locations seeds it via
  // the ?panel=locations query and then strips the param.
  const [locationsOpen, setLocationsOpen] = useState(
    () => searchParams.get('panel') === 'locations',
  );
  useEffect(() => {
    if (searchParams.get('panel') === 'locations') {
      setLocationsOpen(true);
      const params = new URLSearchParams(searchParams);
      params.delete('panel');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  function setView(next: CalendarView) {
    const params = new URLSearchParams(searchParams);
    if (next === 'week') params.delete('view');
    else params.set('view', next);
    // template id only applies in template mode; switching calendar
    // sub-view clears any stale template selection.
    params.delete('template');
    setSearchParams(params, { replace: true });
  }

  function setMode(next: Mode) {
    const params = new URLSearchParams(searchParams);
    if (next === 'calendar') {
      params.delete('mode');
      params.delete('template');
    } else {
      params.set('mode', next);
      // Calendar sub-view is meaningless in template mode — drop it so
      // the URL stays canonical.
      params.delete('view');
    }
    setSearchParams(params, { replace: true });
  }

  function setSelectedTemplate(id: string | null) {
    const params = new URLSearchParams(searchParams);
    if (id) {
      params.set('template', id);
      params.set('mode', 'template');
      params.delete('view');
    } else {
      params.delete('template');
    }
    setSearchParams(params, { replace: true });
  }

  const [cursor, setCursor] = useState(() => isoMonday(new Date()));
  const [creating, setCreating] = useState(false);
  // The sheet opens on activity click and stays decoupled from the
  // `/schedule/activities/:id` route — the route is kept as a deep-link
  // fallback (notification bell URLs etc.) but in-context the sheet is
  // the primary surface so the user never leaves the calendar.
  const [openActivityId, setOpenActivityId] = useState<string | null>(null);
  const [createDefault, setCreateDefault] = useState<Date | undefined>(undefined);
  // Template create state lives at the page level so the "+ Ny mall" button
  // can sit in the toolbar row alongside the view tabs (rather than crammed
  // back inside the TemplatesView body). The inline name form renders just
  // below the toolbar when `templateCreating` is true.
  const [templateCreating, setTemplateCreating] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // Opens the AI-driven template generator. The button sits in the
  // toolbar alongside "+ Ny mall" — same gating (templates view only).
  const [generateOpen, setGenerateOpen] = useState(false);
  // resumeDraft=<job_id> in the URL opens the dialog straight into the
  // polling/loading step so a notification-bell click on a completed
  // template_draft_* row deep-links back to the preview. Cleared from the
  // URL when the dialog closes so a page reload doesn't keep re-opening.
  const resumeDraftJobId = searchParams.get('resumeDraft');
  useEffect(() => {
    if (resumeDraftJobId) setGenerateOpen(true);
  }, [resumeDraftJobId]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
  // Team filter is URL-driven via ?team=<uuid>[,<uuid>...]. Lets bell
  // notifications and the AI assistant's "open this team's schedule" links
  // deep-link straight into a filtered calendar, and makes filtered views
  // shareable/back-button-able. Mirror effect below keeps URL ↔ state in
  // sync after the initial seed.
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(() => {
    const raw = searchParams.get('team');
    if (!raw) return new Set();
    return new Set(raw.split(',').filter(Boolean));
  });
  const [selectedZoneIds, setSelectedZoneIds] = useState<Set<string>>(new Set());
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<Set<ActivityType>>(new Set());
  // Mobile/tablet filter sheet. The inline filter sidebar is `lg`-only;
  // below that the same panel slides in from the right via FilterDrawer's
  // Sheet branch, controlled by this state.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showApply, setShowApply] = useState(false);

  const range = useMemo(() => {
    if (calendarView === 'month') {
      // Cover the entire dayGridMonth canvas. The grid's first cell is
      // the Monday on/before the 1st of cursor's month; 6 weeks (42 days)
      // is the maximum grid size FullCalendar will render.
      const monthStart = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
      const gridStart = isoMonday(monthStart);
      const from = new Date(gridStart);
      const to = new Date(gridStart);
      to.setUTCDate(gridStart.getUTCDate() + 42);
      return { from, to };
    }
    const from = new Date(cursor);
    const to = new Date(cursor);
    to.setUTCDate(from.getUTCDate() + 7);
    return { from, to };
  }, [cursor, calendarView]);

  const { data: activities = [] } = useQuery({
    queryKey: ['activities', slug, range.from.toISOString(), calendarView],
    queryFn: () => listActivitiesInRange(token, slug, range.from, range.to),
    enabled: !!slug && mode === 'calendar',
  });

  // Opponent / crest / home-away / score for the matches on screen, plus our
  // own crest, so the calendar renders a game rather than a lone team name.
  const fixtures = useActivityFixtures(token, slug, activities);
  const orgCrestUrl = useOrgCrest(token, slug);

  // Same cache key as LocationsPanel — no duplicate network requests when
  // the panel is also open.
  const { data: locationsRes } = useQuery({
    queryKey: ['locations', slug],
    queryFn: () => listLocations(token, slug),
    enabled: !!slug,
  });
  const locations = locationsRes?.locations ?? [];

  const { data: templates = [] } = useQuery({
    queryKey: ['templates', slug],
    queryFn: () => listTemplates(token, slug),
    enabled: !!slug,
  });

  function shiftWeeks(n: number) {
    const next = new Date(cursor);
    next.setUTCDate(cursor.getUTCDate() + 7 * n);
    setCursor(next);
  }

  function shiftMonths(n: number) {
    // Anchor on the 15th of the target month so the result is always
    // inside that month (avoids month-overflow when the source day-of-
    // month exceeds the target's length), then snap back to Monday so
    // cursor stays valid for the week/list/resources views.
    const next = new Date(Date.UTC(
      cursor.getUTCFullYear(),
      cursor.getUTCMonth() + n,
      15,
    ));
    setCursor(isoMonday(next));
  }

  const shiftPage = (n: number) =>
    calendarView === 'month' ? shiftMonths(n) : shiftWeeks(n);

  // Refetch everything the grid renders. The fixture cache is keyed per
  // activity, so a match whose opponent/crest just changed needs its own
  // invalidation — the activity row alone doesn't carry the scoreboard.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['activities', slug] });
    void qc.invalidateQueries({ queryKey: ['activity-fixture', slug] });
  };

  function toggleLocation(id: string) {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        // Unchecking a location clears any of its zones from the zone
        // filter too — the zone checkboxes only render under the
        // location, so leaving stale zones selected would create a
        // ghost filter the user can't see.
        const loc = locations.find((l) => l.id === id);
        if (loc) {
          setSelectedZoneIds((zPrev) => {
            const zNext = new Set(zPrev);
            for (const z of loc.zones) zNext.delete(z.id);
            return zNext;
          });
        }
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function selectLocations(ids: string[]) {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }

  function clearLocations() {
    setSelectedLocationIds(new Set());
    // Zones only render under their location, so clearing locations must
    // clear zone filters too — otherwise a hidden zone filter would
    // silently shrink the result set.
    setSelectedZoneIds(new Set());
  }

  function toggleTeam(id: string) {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectTeams(ids: string[]) {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }

  function clearTeams() {
    setSelectedTeamIds(new Set());
  }

  function toggleZone(id: string) {
    setSelectedZoneIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleActivityType(kind: ActivityType) {
    setSelectedActivityTypes((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  // Block-notice plumbing — fires when a click/drop/resize lands in a
  // greyed region. The notice auto-dismisses; only one is shown at a
  // time and back-to-back blocks restart the timer.
  const { notice: blockNotice, onBlocked: onCalendarBlocked, clear: clearBlockNotice } =
    useAvailabilityBlockNotice();

  const filteredActivities = useMemo(() => {
    let result = activities;
    if (selectedLocationIds.size > 0) {
      result = result.filter((a) => !!a.location_id && selectedLocationIds.has(a.location_id));
    }
    if (selectedZoneIds.size > 0) {
      result = result.filter((a) => !!a.zone_id && selectedZoneIds.has(a.zone_id));
    }
    if (selectedTeamIds.size > 0) {
      result = result.filter((a) => a.team_ids.some((id) => selectedTeamIds.has(id)));
    }
    if (selectedActivityTypes.size > 0) {
      result = result.filter((a) => selectedActivityTypes.has(a.activity_type));
    }
    return result;
  }, [activities, selectedLocationIds, selectedZoneIds, selectedTeamIds, selectedActivityTypes]);

  // The resources view always renders every location as its own row, so
  // applying the location filter would hide the very thing the view is
  // meant to show. We keep the other filters (team, zone, activity type)
  // because those filter the activity *cards*, not the resource rows.
  const resourcesActivities = useMemo(() => {
    let result = activities;
    if (selectedZoneIds.size > 0) {
      result = result.filter((a) => !!a.zone_id && selectedZoneIds.has(a.zone_id));
    }
    if (selectedTeamIds.size > 0) {
      result = result.filter((a) => a.team_ids.some((id) => selectedTeamIds.has(id)));
    }
    if (selectedActivityTypes.size > 0) {
      result = result.filter((a) => selectedActivityTypes.has(a.activity_type));
    }
    return result;
  }, [activities, selectedZoneIds, selectedTeamIds, selectedActivityTypes]);

  // Opening-hours overlay for the calendar. Only kicks in when the user
  // filters down to exactly one location — with two+ the rules would
  // overlap into noise, and with zero the calendar should stay neutral.
  // The fetched rules are converted to FullCalendar businessHours so the
  // grid renders out-of-hours regions in a muted background colour.
  const singleLocationId = selectedLocationIds.size === 1
    ? Array.from(selectedLocationIds)[0] ?? null
    : null;

  const { data: availability } = useQuery({
    queryKey: ['locations', slug, singleLocationId, 'availability'],
    queryFn: () => getAvailability(token, slug, singleLocationId!),
    enabled: !!slug && !!singleLocationId,
  });

  // Per-location availability for the resources view — fans out to N
  // parallel queries (one per location) and shares the same cache key
  // as the single-location FullCalendar fetch, so opening a location's
  // detail page later is a free hit. Gated on calendar+resources view to
  // skip the fan-out on every page load.
  const inResourcesView = mode === 'calendar' && view === 'resources';
  const resourcesAvailabilityResults = useQueries({
    queries: locations.map((loc) => ({
      queryKey: ['locations', slug, loc.id, 'availability'],
      queryFn: () => getAvailability(token, slug, loc.id),
      enabled: !!slug && inResourcesView,
    })),
  });
  const resourcesAvailability = useMemo(() => {
    const m = new Map<string, AvailabilityResponse>();
    resourcesAvailabilityResults.forEach((r, i) => {
      const loc = locations[i];
      if (loc && r.data) m.set(loc.id, r.data);
    });
    return m;
    // We depend on the data array's identity via map; the linter would
    // want every result, but that explodes the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, resourcesAvailabilityResults.map((r) => r.data).join('|')]);

  // Zone-aware businessHours + closed-day exceptions.
  //
  // Precedence matches the server-side scope predicate: if exactly one
  // zone is filtered and that zone has its own rules, use them. If the
  // zone has none, fall through to location-level rules (the "no override
  // = inherit" rule the server enforces in CheckAvailability). With zero
  // or multiple zones selected, we always fall back to location-level so
  // the greying represents the broader scope the user is looking at.
  const singleZoneId = selectedZoneIds.size === 1
    ? Array.from(selectedZoneIds)[0] ?? null
    : null;

  const businessHours = useMemo(() => {
    if (!availability) return undefined;
    // FullCalendar expects daysOfWeek with Sun=0, Sat=6; our API uses
    // ISO Mon=1..Sun=7. Map 7→0.
    const zoneRules = singleZoneId ? availability.rules[singleZoneId] ?? [] : [];
    const locRules = availability.rules[''] ?? [];
    const rules = zoneRules.length > 0 ? zoneRules : locRules;
    if (rules.length === 0) return undefined;
    return rules.map((r) => ({
      daysOfWeek: [r.day_of_week === 7 ? 0 : r.day_of_week],
      startTime: r.start_time,
      endTime: r.end_time,
    }));
  }, [availability, singleZoneId]);

  // Closed-day exceptions render as full-day background events so they
  // visually stand out on top of the businessHours muting (a normal
  // off-hours day looks lightly greyed; a closed-by-exception day looks
  // unmistakably blocked). Same zone-overrides-location precedence as
  // rules. Hours-replace exceptions aren't surfaced yet — FullCalendar's
  // businessHours has no per-date override, so doing them right would
  // need a per-day computed greyed range.
  const closedExceptionEvents = useMemo(() => {
    if (!availability) return undefined;
    const zoneExc = singleZoneId ? availability.exceptions[singleZoneId] ?? [] : [];
    const locExc = availability.exceptions[''] ?? [];
    const exceptions = zoneExc.length > 0 ? zoneExc : locExc;
    const closed = exceptions.filter((e) => e.kind === 'closed');
    if (closed.length === 0) return undefined;
    return closed.map((e) => ({
      // 00:00 → next-day 00:00 covers the whole day in the time-grid.
      // End-exclusive matches FullCalendar's convention so the event
      // doesn't leak into the next day's first slot.
      start: `${e.on_date}T00:00:00`,
      end: `${e.on_date}T23:59:59`,
      display: 'background' as const,
      classNames: ['fc-availability-closed'],
    }));
  }, [availability, singleZoneId]);

  const createMut = useMutation({
    mutationFn: async (args: { input: ActivityInput; match: MatchDetailsValue | null }) => {
      const activity = await createActivity(token, slug, args.input);
      // Two-step for matches: the fixture attaches to the new activity id.
      if (args.match && hasOpponent(args.match)) {
        await persistMatchDetails(token, slug, activity.id, args.match);
      }
      return activity;
    },
    onSuccess: () => {
      setCreating(false);
      setCreateDefault(undefined);
    },
    // onSettled, not onSuccess: the create is two calls, and a failed fixture
    // step still leaves a created activity behind. Invalidating either way
    // means the grid always shows what's actually in the DB.
    onSettled: invalidate,
  });

  const updateMut = useMutation({
    mutationFn: async (args: { id: string; input: ActivityInput; match?: MatchDetailsValue | null }) => {
      const activity = await updateActivity(token, slug, args.id, args.input);
      // On edit the activity already exists — persist any match-detail changes
      // (skip when the opponent was left blank: a TBD fixture stays TBD).
      if (args.match && hasOpponent(args.match)) {
        await persistMatchDetails(token, slug, args.id, args.match);
      }
      return activity;
    },
    // Sheet stays open after save so the user sees the fresh data; invalidate
    // triggers the activity + fixture + calling queries to refetch. Settled
    // rather than success for the same reason as create above.
    onSettled: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteActivity(token, slug, id, ''),
    onSuccess: () => {
      setOpenActivityId(null);
      invalidate();
    },
  });

  const createTemplateMut = useMutation({
    mutationFn: (n: string) => createTemplate(token, slug, { name: n }),
    onSuccess: () => {
      setTemplateCreating(false);
      setTemplateName('');
      qc.invalidateQueries({ queryKey: ['templates', slug] });
    },
  });

  async function handleReschedule(id: string, startsAt: Date, endsAt: Date) {
    await rescheduleActivity(token, slug, id, {
      starts_at: startsAt.toISOString(),
      ends_at: endsAt.toISOString(),
    });
    invalidate();
  }

  function describeError(err: unknown): string | null {
    if (!err) return null;
    if (err instanceof ApiError) {
      if (err.code === 'schedule_conflict') return t('schedule.toast.createConflict');
      if (err.code === 'forbidden_by_scope') return t('schedule.validation.teamRequired');
      if (err.code === 'invalid_input') return t('locations.errors.generic');
    }
    return t('locations.errors.generic');
  }

  const language = i18n.resolvedLanguage ?? 'en';
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(language, { weekday: 'short', day: 'numeric', month: 'short' }),
    [language],
  );

  const headerLabel = useMemo(() => {
    if (calendarView === 'month') {
      const m = new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(cursor);
      return capitaliseFirst(m);
    }
    const end = new Date(cursor);
    end.setUTCDate(cursor.getUTCDate() + 6);
    return [capitaliseFirst(dayFmt.format(cursor)), capitaliseFirst(dayFmt.format(end))].join(' – ');
  }, [cursor, dayFmt, language, calendarView]);

  const onActivityClick = (id: string) => {
    setOpenActivityId(id);
  };

  const showCalendar = mode === 'calendar';
  const showResources = mode === 'calendar' && view === 'resources';

  // Resources view auto-selects every team + activity type on entry so
  // the filter checkboxes match what the view is actually showing
  // ("0 selected but all visible" reads as a bug otherwise). When the
  // user leaves resources view, we restore the snapshot so the other
  // views' filter state isn't clobbered.
  //
  // Teams are fetched separately (high limit) — the regular team filter
  // is server-paginated and doesn't have every ID on hand.
  const { data: allTeamsForResources } = useQuery({
    queryKey: ['teams-all', slug],
    queryFn: () => listTeams(token, slug, { limit: 500 }),
    enabled: !!slug && showResources,
  });

  const filterSnapshotRef = useRef<{
    teams: Set<string>;
    types: Set<ActivityType>;
  } | null>(null);
  const teamsAutoAppliedRef = useRef(false);

  useEffect(() => {
    if (showResources) {
      if (filterSnapshotRef.current === null) {
        filterSnapshotRef.current = {
          teams: new Set(selectedTeamIds),
          types: new Set(selectedActivityTypes),
        };
        setSelectedActivityTypes(new Set(ACTIVITY_TYPES));
      }
    } else if (filterSnapshotRef.current !== null) {
      setSelectedTeamIds(filterSnapshotRef.current.teams);
      setSelectedActivityTypes(filterSnapshotRef.current.types);
      filterSnapshotRef.current = null;
      teamsAutoAppliedRef.current = false;
    }
    // We intentionally don't include selectedTeamIds/selectedActivityTypes
    // in the dep array — the effect should only fire on view transitions,
    // not every time the user toggles a checkbox.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResources]);

  useEffect(() => {
    if (
      showResources &&
      allTeamsForResources &&
      filterSnapshotRef.current !== null &&
      !teamsAutoAppliedRef.current
    ) {
      setSelectedTeamIds(new Set(allTeamsForResources.teams.map((tm) => tm.id)));
      teamsAutoAppliedRef.current = true;
    }
  }, [showResources, allTeamsForResources]);

  // Mirror selectedTeamIds into ?team=… so a filtered view is shareable,
  // back-button-able, and addressable from notification deep-links. Skipped
  // while resources mode is active because that view auto-selects every team
  // as a display superset (not a user filter choice) — the snapshot ref
  // restores the real selection on exit, at which point this effect fires
  // again and writes it back to the URL.
  useEffect(() => {
    if (showResources) return;
    const desired =
      selectedTeamIds.size === 0
        ? null
        : Array.from(selectedTeamIds).sort().join(',');
    const current = searchParams.get('team');
    if (desired === current) return;
    const next = new URLSearchParams(searchParams);
    if (desired === null) next.delete('team');
    else next.set('team', desired);
    setSearchParams(next, { replace: true });
  }, [selectedTeamIds, showResources, searchParams, setSearchParams]);
  // FullCalendar canvas only renders for the FC-backed sub-views.
  // 'resources' has its own grid (built in phase C) so we gate the
  // calendar block out when it's selected.
  const showFullCalendar = showCalendar && !showResources;
  // Filter UI is meaningful in any branch that renders the FilterDrawer
  // (calendar, resources, template editor). The templates-list view has
  // no filterable canvas, so we hide the trigger there. The team count
  // isn't checked here — the CalendarFilterPanel queries teams itself
  // and renders "no results" if the org has none; gating on locations
  // alone would hide the filter for orgs that have teams but no
  // locations (rare but valid mid-onboarding).
  const showFilters =
    showFullCalendar || showResources || (mode === 'template' && !!selectedTemplateId);
  // Resources view ignores the location filter (every location always
  // renders as a row) and hides the locations+zones section in the panel,
  // so don't count those toward the badge there — otherwise it'd show a
  // phantom count for filters the user can't see or clear.
  const activeFilterCount =
    (showResources ? 0 : selectedLocationIds.size) +
    selectedTeamIds.size +
    selectedZoneIds.size +
    selectedActivityTypes.size;

  const actionsBlock = (
    <div className="flex flex-wrap items-center gap-2">
      {/* Primary CTA stays visible at every viewport. Apply lives
          here in the template-editor branch (its destination is the
          calendar, so it reads as the action you'd take). */}
      {showCalendar && (
        <Button
          onClick={() => {
            setCreateDefault(undefined);
            setCreating(true);
          }}
        >
          <Plus weight="bold" />
          {t('schedule.newActivity')}
        </Button>
      )}
      {!showCalendar && !selectedTemplateId && (
        <Button
          onClick={() => {
            setTemplateName('');
            setTemplateCreating(true);
          }}
        >
          <Plus weight="bold" />
          {t('templates.newTemplate')}
        </Button>
      )}
      {!showCalendar && selectedTemplateId && (
        <Button onClick={() => setShowApply(true)}>{t('templates.apply')}</Button>
      )}

      {/* Secondary actions — folded into a kebab overflow menu on
          narrow viewports so the action row never wraps to two lines.
          Template mode carries three wide buttons (locations, team
          categories, AI generate) so it collapses later, at lg; calendar
          mode only has the single locations button so it can stay inline
          from sm up. The action set is conditional on which sub-view is
          active (calendar / template list / template editor). */}
      <div
        className={`hidden items-center gap-2 ${showCalendar ? 'sm:flex' : 'lg:flex'}`}
      >
        <Button
          variant="outline"
          onClick={() => setLocationsOpen(true)}
          aria-label={t('locations.title')}
        >
          <MapPin weight="bold" />
          {t('locations.title')}
        </Button>
        {!showCalendar ? (
          <>
            <Button
              variant="outline"
              onClick={() => navigate(`/orgs/${slug}/settings/team-categories`)}
            >
              <Tag weight="bold" />
              {t('nav.settingsTeamCategories')}
            </Button>
            <Button variant="outline" onClick={() => setGenerateOpen(true)}>
              <Sparkle weight="bold" />
              {t('templateGenerate.toolbarCta')}
            </Button>
          </>
        ) : null}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t('rowActions.openMenu')}
          className={`flex h-9 w-9 items-center justify-center rounded-pill border border-input bg-background text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${showCalendar ? 'sm:hidden' : 'lg:hidden'}`}
        >
          <DotsThree weight="bold" className="h-5 w-5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setLocationsOpen(true)}>
            <MapPin weight="bold" className="h-4 w-4" />
            {t('locations.title')}
          </DropdownMenuItem>
          {!showCalendar ? (
            <>
              <DropdownMenuItem
                onSelect={() => navigate(`/orgs/${slug}/settings/team-categories`)}
              >
                <Tag weight="bold" className="h-4 w-4" />
                {t('nav.settingsTeamCategories')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setGenerateOpen(true)}>
                <Sparkle weight="bold" className="h-4 w-4" />
                {t('templateGenerate.toolbarCta')}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">
            {t('schedule.title')}
          </h1>
          <p className="text-sm text-foreground/70">{t('schedule.tagline')}</p>
        </div>
        {/* In template mode the actions move down to the tab row (right
            side, opposite the mode tabs) — that row's center/right is empty
            there. Calendar mode keeps them here since its tab row already
            carries the date nav + view tabs + filter. */}
        {showCalendar && actionsBlock}
      </header>

      {/* Row 2: mode tabs (left) + date nav (center) + view tabs (right) —
          directly above the calendar. Three-column grid keeps the date nav
          truly centered on the page regardless of the side tab widths.
          Falls back to wrapping flex on narrow viewports. */}
      <div className="flex flex-wrap items-center justify-between gap-3 md:grid md:grid-cols-[1fr_auto_1fr]">
        {/* Left: mode tabs (Calendar / Weekly template). Mirrors the view
            tab strip on the right so the two axes of the workspace read
            symmetrically. */}
        <div className="flex md:justify-self-start">
          <div
            ref={modeTabListRef}
            role="tablist"
            aria-label={t('schedule.mode.ariaLabel')}
            className="relative flex items-center gap-1 rounded-pill bg-muted p-1"
          >
            <span
              ref={modeTabGliderRef}
              aria-hidden
              className="pointer-events-none absolute bottom-1 top-1 left-0 rounded-pill bg-card shadow-xs transition-[width,transform] duration-200 ease-out"
            />
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                onClick={() => setMode(m)}
                className={
                  mode === m
                    ? 'relative z-10 rounded-pill px-3 py-1 text-sm font-semibold'
                    : 'relative z-10 rounded-pill px-3 py-1 text-sm font-semibold text-foreground/70 hover:text-foreground'
                }
              >
                {t(`schedule.mode.${m}`)}
              </button>
            ))}
          </div>
        </div>
        {/* Center: week navigation (calendar) or template back-link
            (template editor). */}
        <div className="md:justify-self-center">
          {showCalendar ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                aria-label={t('common.back')}
                onClick={() => shiftPage(-1)}
              >
                <CaretLeft weight="bold" />
              </Button>
              <span className="font-heading text-sm font-semibold">{headerLabel}</span>
              <Button
                variant="outline"
                size="icon"
                aria-label={t('common.continue')}
                onClick={() => shiftPage(1)}
              >
                <CaretRight weight="bold" />
              </Button>
            </div>
          ) : selectedTemplateId ? (
            <span className="font-heading text-sm font-semibold">
              {templates.find((t2) => t2.id === selectedTemplateId)?.name ?? ''}
            </span>
          ) : null}
        </div>
        {/* Right: calendar sub-view tabs (only when mode=calendar) + the
            mobile/tablet filter trigger. In template mode the view tabs
            collapse because there's only one canvas (the editor). */}
        <div className="flex flex-wrap items-center gap-2 md:justify-self-end">
          {showCalendar && (
            <div
              ref={tabListRef}
              role="tablist"
              aria-label={t('schedule.title')}
              className="relative flex items-center gap-1 rounded-pill bg-muted p-1"
            >
              <span
                ref={tabGliderRef}
                aria-hidden
                className="pointer-events-none absolute bottom-1 top-1 left-0 rounded-pill bg-card shadow-xs transition-[width,transform] duration-200 ease-out"
              />
              {CALENDAR_VIEWS.map((v) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={view === v}
                  onClick={() => setView(v)}
                  className={
                    view === v
                      ? 'relative z-10 rounded-pill px-3 py-1 text-sm font-semibold'
                      : 'relative z-10 rounded-pill px-3 py-1 text-sm font-semibold text-foreground/70 hover:text-foreground'
                  }
                >
                  {t(`schedule.view.${v}`)}
                </button>
              ))}
            </div>
          )}
          {showFilters && (
            <Button
              variant="outline"
              onClick={() => setFiltersOpen(true)}
              className="lg:hidden"
              aria-label={t('schedule.filter.title')}
            >
              <Funnel weight="bold" />
              {t('schedule.filter.title')}
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          )}
          {/* Template mode: the toolbar actions sit here, opposite the mode
              tabs on the left — same row rather than up in the header. */}
          {!showCalendar && actionsBlock}
        </div>
      </div>

      {/* Inline name form for "+ Ny mall". Sits between the toolbar and
          the templates table when active. */}
      {mode === 'template' && !selectedTemplateId && templateCreating && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = templateName.trim();
            if (!trimmed) return;
            createTemplateMut.mutate(trimmed);
          }}
          className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card p-4"
        >
          <label className="flex-1">
            <span className="font-heading text-sm font-semibold">
              {t('locations.form.name')}
            </span>
            <Input
              autoFocus
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setTemplateCreating(false);
                setTemplateName('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={createTemplateMut.isPending}>
              {createTemplateMut.isPending
                ? t('locations.form.saving')
                : t('locations.form.save')}
            </Button>
          </div>
        </form>
      )}

      {showFullCalendar ? (
        <div className="flex items-start gap-3">
          <div className="relative min-w-0 flex-1">
            {selectedLocationIds.size === 0 && selectedTeamIds.size === 0 ? (
              // Empty canvas — the user hasn't ticked any location or
              // team yet. Either filter alone is enough to render: a
              // team filter naturally scopes to that team's activities
              // across all locations (one team can't be in two places
              // at once, so there's no visual collision risk).
              <SelectLocationEmptyState
                locationsExist={locations.length > 0}
                onOpenLocations={() => setLocationsOpen(true)}
                t={t}
              />
            ) : (
              <>
                <ScheduleCalendar
                  view={calendarView}
                  date={cursor}
                  activities={filteredActivities}
                  allActivities={activities}
                  locations={locations}
                  fixtures={fixtures}
                  orgCrestUrl={orgCrestUrl}
                  {...(businessHours ? { businessHours } : {})}
                  {...(closedExceptionEvents ? { backgroundEvents: closedExceptionEvents } : {})}
                  onActivityClick={onActivityClick}
                  onReschedule={handleReschedule}
                  onBlocked={onCalendarBlocked}
                  onDateClick={(when) => {
                    setCreateDefault(when);
                    setCreating(true);
                  }}
                />
                {blockNotice && (
                  <AvailabilityBlockNotice
                    key={blockNotice.key}
                    reason={blockNotice.reason}
                    onDismiss={clearBlockNotice}
                  />
                )}
              </>
            )}
          </div>
          <FilterDrawer open={filtersOpen} onOpenChange={setFiltersOpen}>
            <CalendarFilterPanel
              slug={slug}
              locations={locations}
              selectedLocationIds={selectedLocationIds}
              selectedTeamIds={selectedTeamIds}
              selectedZoneIds={selectedZoneIds}
              selectedActivityTypes={selectedActivityTypes}
              onToggleLocation={toggleLocation}
              onToggleTeam={toggleTeam}
              onToggleZone={toggleZone}
              onToggleActivityType={toggleActivityType}
              onSelectLocations={selectLocations}
              onClearLocations={clearLocations}
              onSelectTeams={selectTeams}
              onClearTeams={clearTeams}
              onClearAll={() => {
                setSelectedLocationIds(new Set());
                setSelectedTeamIds(new Set());
                setSelectedZoneIds(new Set());
                setSelectedActivityTypes(new Set());
              }}
            />
          </FilterDrawer>
        </div>
      ) : showResources ? (
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <ResourcesView
              weekStart={cursor}
              activities={resourcesActivities}
              locations={locations}
              availabilityByLocation={resourcesAvailability}
              onActivityClick={onActivityClick}
            />
          </div>
          <FilterDrawer open={filtersOpen} onOpenChange={setFiltersOpen}>
            <CalendarFilterPanel
              slug={slug}
              locations={locations}
              selectedLocationIds={selectedLocationIds}
              selectedTeamIds={selectedTeamIds}
              selectedZoneIds={selectedZoneIds}
              selectedActivityTypes={selectedActivityTypes}
              hideLocations
              onToggleLocation={toggleLocation}
              onToggleTeam={toggleTeam}
              onToggleZone={toggleZone}
              onToggleActivityType={toggleActivityType}
              onSelectLocations={selectLocations}
              onClearLocations={clearLocations}
              onSelectTeams={selectTeams}
              onClearTeams={clearTeams}
              onClearAll={() => {
                setSelectedLocationIds(new Set());
                setSelectedTeamIds(new Set());
                setSelectedZoneIds(new Set());
                setSelectedActivityTypes(new Set());
              }}
            />
          </FilterDrawer>
        </div>
      ) : selectedTemplateId ? (
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            {selectedLocationIds.size === 0 && selectedTeamIds.size === 0 ? (
              <SelectLocationEmptyState
                locationsExist={locations.length > 0}
                onOpenLocations={() => setLocationsOpen(true)}
                t={t}
              />
            ) : (
              <TemplateEditorView
                slug={slug}
                templateId={selectedTemplateId}
                onBack={() => setSelectedTemplate(null)}
                hideHeader
                selectedLocationIds={selectedLocationIds}
                selectedTeamIds={selectedTeamIds}
                selectedZoneIds={selectedZoneIds}
                selectedActivityTypes={selectedActivityTypes}
              />
            )}
          </div>
          <FilterDrawer open={filtersOpen} onOpenChange={setFiltersOpen}>
            <CalendarFilterPanel
              slug={slug}
              locations={locations}
              selectedLocationIds={selectedLocationIds}
              selectedTeamIds={selectedTeamIds}
              selectedZoneIds={selectedZoneIds}
              selectedActivityTypes={selectedActivityTypes}
              onToggleLocation={toggleLocation}
              onToggleTeam={toggleTeam}
              onToggleZone={toggleZone}
              onToggleActivityType={toggleActivityType}
              onSelectLocations={selectLocations}
              onClearLocations={clearLocations}
              onSelectTeams={selectTeams}
              onClearTeams={clearTeams}
              onClearAll={() => {
                setSelectedLocationIds(new Set());
                setSelectedTeamIds(new Set());
                setSelectedZoneIds(new Set());
                setSelectedActivityTypes(new Set());
              }}
            />
          </FilterDrawer>
        </div>
      ) : (
        <TemplatesView
          slug={slug}
          onSelect={setSelectedTemplate}
          onGenerate={() => setGenerateOpen(true)}
        />
      )}

      {creating && (
        <ActivityFormModal
          slug={slug}
          {...(createDefault ? { defaultStart: createDefault } : {})}
          submitting={createMut.isPending}
          error={describeError(createMut.error)}
          onSubmit={(input, match) => createMut.mutateAsync({ input, match })}
          onCancel={() => {
            setCreating(false);
            setCreateDefault(undefined);
          }}
        />
      )}
      <ActivitySheet
        open={openActivityId !== null}
        onOpenChange={(next) => {
          if (!next) setOpenActivityId(null);
        }}
        slug={slug}
        activityId={openActivityId}
        onSubmit={(id, input, match) => updateMut.mutateAsync({ id, input, match: match ?? null })}
        onDelete={(id) => deleteMut.mutateAsync(id)}
        submitting={updateMut.isPending}
        deleting={deleteMut.isPending}
        submitError={describeError(updateMut.error ?? deleteMut.error)}
      />

      {showApply && selectedTemplateId && (
        <ApplyTemplateModal
          slug={slug}
          token={token}
          templateId={selectedTemplateId}
          onClose={() => setShowApply(false)}
        />
      )}

      <LocationsPanel slug={slug} open={locationsOpen} onOpenChange={setLocationsOpen} />

      <TemplateGenerateDialog
        open={generateOpen}
        onClose={() => {
          setGenerateOpen(false);
          if (resumeDraftJobId) {
            // Strip the param so a page reload doesn't keep reopening.
            const next = new URLSearchParams(searchParams);
            next.delete('resumeDraft');
            setSearchParams(next, { replace: true });
          }
        }}
        slug={slug}
        {...(selectedTemplateId ? { forceTemplateId: selectedTemplateId } : {})}
        {...(resumeDraftJobId ? { resumeJobId: resumeDraftJobId } : {})}
        onCreated={(tid) => setSelectedTemplate(tid)}
      />
    </div>
  );
}

function capitaliseFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// SelectLocationEmptyState — rendered in the calendar/template canvas
// when no location is in selectedLocationIds. Two flavors: a CTA-style
// "add locations first" when no locations exist in the org, and a
// gentler hint "pick a location in the filter on the right" when at
// least one exists. Receives `t` rather than calling useTranslation()
// itself so it stays a pure presentational helper.
function SelectLocationEmptyState({
  locationsExist,
  onOpenLocations,
  t,
}: {
  locationsExist: boolean;
  onOpenLocations: () => void;
  t: (key: string) => string;
}) {
  return (
    <div className="flex h-full min-h-[24rem] items-center justify-center rounded-2xl border border-dashed border-border bg-card p-8 text-center">
      <div className="flex max-w-md flex-col items-center gap-3">
        <MapPin weight="bold" className="h-8 w-8 text-foreground/50" />
        {locationsExist ? (
          <>
            <h3 className="font-heading text-lg font-bold">
              {t('schedule.selectLocation.title')}
            </h3>
            <p className="text-sm text-foreground/70">
              {t('schedule.selectLocation.body')}
            </p>
          </>
        ) : (
          <>
            <h3 className="font-heading text-lg font-bold">
              {t('schedule.selectLocation.noneTitle')}
            </h3>
            <p className="text-sm text-foreground/70">
              {t('schedule.selectLocation.noneBody')}
            </p>
            <Button onClick={onOpenLocations}>
              <Plus weight="bold" />
              {t('locations.newLocation')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function isoMonday(d: Date): Date {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const jsDay = date.getUTCDay();
  const isoDay = jsDay === 0 ? 7 : jsDay;
  date.setUTCDate(date.getUTCDate() - (isoDay - 1));
  return date;
}
